/**
 * Procurement Demo — Nami answers for the overview's suggested questions.
 *
 * THROWAWAY DEMO CODE. The overview used to have its own "Ask procurement"
 * panel; its four suggested questions now live in Nami's empty state. Each one
 * is answered in the chat with the same text as before (`ASK_SUGGESTIONS`), and
 * the cited agreement sections follow as links that open the contract at the
 * clause (the registrar turns those links into in-app navigation). These
 * answers stay on the current page: there is nothing to open afterwards.
 */

import { matchPhrasing } from '../ask/match-scripted';
import { ASK_SUGGESTIONS } from '../pages/overview/ask-content';
import { matchProcurementDemoScript } from './script';
import { contractSourceLink, sourcesLine } from './source-links';

export interface OverviewAnswer {
  /** The suggested question, as shown on the chip. */
  question: string;
  /** Phrasings that start this answer: the question plus any other wording. */
  phrasings: string[];
  /** Markdown: the answer, then a "Sources" line of links to the clauses. */
  narration: string;
}

export const OVERVIEW_ANSWERS: OverviewAnswer[] = ASK_SUGGESTIONS.map(
  (suggestion) => ({
    question: suggestion.question,
    phrasings: [suggestion.question, ...(suggestion.aliases ?? [])],
    narration: `${suggestion.answer}${sourcesLine(suggestion.citations.map(contractSourceLink))}`,
  }),
);

/**
 * The overview answer for a user message, or undefined if it is not one.
 *
 * A chip's own wording always gets its answer. Any other wording that an older
 * Nami prompt already handles (and then opens a page for, such as "What service
 * levels does Gently AI commit to?") keeps going there instead.
 */
export function matchOverviewAnswer(
  message: string,
): OverviewAnswer | undefined {
  const answer = matchPhrasing(
    message,
    OVERVIEW_ANSWERS,
    (candidate) => candidate.phrasings,
  );
  if (!answer) return undefined;
  const isChipWording = OVERVIEW_ANSWERS.some(
    (candidate) => candidate.question === message,
  );
  if (isChipWording) return answer;
  return matchProcurementDemoScript(message) === null ? answer : undefined;
}
